/**
 * Lightning, from the GOES-19 Geostationary Lightning Mapper.
 *
 * ## What this data IS, and what it is not
 *
 * GLM sees **total lightning** — in-cloud, cloud-to-cloud and cloud-to-ground alike —
 * from geostationary orbit, at roughly 10 km resolution. It is NOT the cloud-to-ground
 * strike data people picture from television weather, which comes from ground networks
 * like NLDN and locates a strike point to within a few hundred metres.
 *
 * That distinction is the whole reason this file carries labels as exported constants:
 * a dot on a map invites the reading "lightning hit exactly there", and for GLM that
 * reading is wrong by up to ten kilometres and may describe a flash that never reached
 * the ground at all. Every surface that draws these must say so.
 *
 * It is still the right data for the decision a county actually makes. Total lightning
 * rises before cloud-to-ground activity does, so "is this storm electrically active and
 * how far away is it" — the question behind pulling a crew off a field — is answered
 * earlier by GLM than by a ground network.
 *
 * Source: NOAA Open Data Dissemination, public domain, no key and no licence terms.
 */
import { haversineMiles } from './eta.js';
/**
 * Wording that must appear wherever flashes are drawn. Exported rather than written
 * into each component so it cannot drift into something more confident on one screen.
 */
export const LIGHTNING_SOURCE_LABEL = 'GOES-19 satellite total lightning';
export const LIGHTNING_CAVEAT = 'Satellite-detected total lightning (in-cloud and cloud-to-ground) at about 10 km accuracy. Positions are approximate and are not ground strike points.';
/** The shortest honest version, for a map chip where there is no room for the rest. */
export const LIGHTNING_CAVEAT_SHORT = 'Satellite total lightning, ~10 km — not exact strike points';
/** How long a flash stays on screen. Beyond this it says nothing about the current storm. */
export const LIGHTNING_RETENTION_MIN = 30;
/**
 * Distance bands for the "how close is it" readout.
 *
 * Six miles is the usual threshold for suspending outdoor activity, and thirty minutes
 * after the last flash is the usual all-clear — hence the retention above. These are
 * offered as defaults, not as advice: the county sets its own threshold.
 */
export const LIGHTNING_NEAR_MILES_DEFAULT = 8;
export const LIGHTNING_NEAR_CHOICES = [5, 8, 10, 15, 20];
/** Drop flashes older than the retention window. */
export function liveFlashes(flashes, now = Date.now(), retentionMin = LIGHTNING_RETENTION_MIN) {
    const cutoff = now - retentionMin * 60_000;
    return flashes.filter((f) => {
        const t = Date.parse(f.at);
        // An undated flash is dropped rather than kept: it cannot be aged, and an
        // unageable dot would sit on the map claiming to be current forever.
        return Number.isFinite(t) && t >= cutoff && t <= now + 60_000;
    });
}
/** Closest flash to a point, or null when there is none in range. */
export function nearestFlash(flashes, point, now = Date.now()) {
    let best = null;
    for (const f of flashes) {
        const miles = haversineMiles(point, [f.lon, f.lat]);
        if (best && miles >= best.miles)
            continue;
        const t = Date.parse(f.at);
        best = {
            miles,
            minutesAgo: Number.isFinite(t) ? Math.max(0, Math.ceil((now - t) / 60_000)) : 0,
            flash: f,
        };
    }
    return best;
}
/**
 * How a distance should read on screen.
 *
 * Deliberately coarse. GLM locates a flash to about 10 km, so printing "6.2 mi" implies
 * a precision the instrument does not have; the band is the honest unit.
 */
export function lightningBand(miles) {
    // 1.5 mi, matching the threshold formatLightningDistance uses for the same word.
    // The two appear side by side on the lightning card, and at 3 mi they disagreed:
    // "about 2 mi" labelled "Overhead", which reads as a contradiction and quietly
    // undermines both numbers.
    if (miles <= 1.5)
        return 'overhead';
    if (miles <= 8)
        return 'close';
    if (miles <= 15)
        return 'nearby';
    return 'distant';
}
export const LIGHTNING_BAND_LABELS = {
    overhead: 'Overhead',
    close: 'Close',
    nearby: 'Nearby',
    distant: 'Distant',
};
/** "about 6 miles" — rounded to the instrument's actual resolution, not to a decimal. */
export function formatLightningDistance(miles) {
    if (miles < 1.5)
        return 'overhead';
    const rounded = miles < 10 ? Math.round(miles) : Math.round(miles / 5) * 5;
    return `about ${rounded} mi`;
}
//# sourceMappingURL=lightning.js.map